import { useServiceReducer, Action, State } from './fetchReducer';
import { useCallback } from 'react';

import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

interface Options {
  id?: string;
  name?: string | null;
  log?: {
    start?: boolean;
    success?: boolean;
    error?: boolean;
  };
  throwErrors?: boolean;
}

const DEFAULT_OPTIONS: Options = {
  name: null,
  log: {
    start: false,
    success: false,
    error: false,
  },
  throwErrors: false,
};

const log = (id: any, message: Action, level = 'log') => {
  // eslint-disable-next-line no-console
  console.group(`Fetch ID: ${id}`);
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  // eslint-disable-next-line no-console
  console[level](message);
  // eslint-disable-next-line no-console
  console.groupEnd();
};

const actions = {
  start: (): Action => ({ type: 'START' }),
  error: (error: any): Action => ({ type: 'ERROR', error }),
  success: (response: any): Action => ({ type: 'SUCCESS', response }),
  reset: (): Action => ({ type: 'RESET' }),
};

type Service = (...args: any[]) => any;

type Reset = () => void;

const useService = (
  service: any,
  options?: Options,
): [Service, State, Reset] => {
  const _options = _.defaultsDeep({ ...options }, DEFAULT_OPTIONS);
  const id = _options.id || uuidv4();
  const [fetchState, dispatch] = useServiceReducer();
  const reset = () => {
    dispatch(actions.reset());
  };

  const executor = useCallback(
    async (...args) => {
      let _response;
      try {
        dispatch(actions.start());
        _options.log.start && log(id, actions.start());
        _response = await service(...args);
        dispatch(actions.success(_response));
        _options.log.success && log(id, actions.success(_response));
        return _response;
      } catch (error) {
        dispatch(actions.error(error));
        _options.log.error && log(id, actions.error(error));
        if (_options.throwErrors) throw error;
        return _response;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [service],
  );
  return [executor, fetchState, reset];
};

export default useService;
